import * as React from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { hasPermission, useAuth, useSession } from '@cguard/auth';
import {
  AccountMenu,
  DropdownMenuItem,
  Toaster,
  Wordmark,
  useDocumentTitle,
} from '@cguard/design-system';
import { formatTime, useShellLabels } from '@cguard/i18n';
import { ArrowLeft, Camera, Clock3, Settings2, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const APP_NAME = 'CGuard · Video Console';

const ROUTE_TITLE_KEYS: Record<string, string> = {
  '/live': 'nav.live',
  '/playback': 'nav.playback',
  '/exports': 'nav.exports',
  '/admin/cameras': 'admin.cameras.title',
  '/admin/analytics': 'admin.analytics.title',
};

export function AppShell(): React.JSX.Element {
  const { t } = useTranslation('video');
  const shell = useShellLabels();
  const { signOut } = useAuth();
  const session = useSession();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isAdministration = pathname.startsWith('/admin/');
  const canAdminister = hasPermission(session, 'cameras.manage');
  const titleKey = ROUTE_TITLE_KEYS[pathname];
  const name = session?.displayName;

  useDocumentTitle(titleKey ? t(titleKey) : undefined, APP_NAME);

  return (
    <div className="video-app" data-app="video-console">
      <header className={`top-rail ${isAdministration ? 'admin-top-rail' : ''}`}>
        <div className="flex min-w-0 items-center gap-4">
          <Wordmark variant="compact" href="/live" />
          <div className="h-5 w-px bg-[var(--color-hairline-strong)]" />
          <span className="truncate font-mono text-2xs uppercase tracking-[0.2em] text-[var(--color-foreground-3)]">
            {t('shell.product')}
          </span>
        </div>

        {!isAdministration ? (
          <nav aria-label={t('nav.workspace')} className="workspace-nav">
            <TopLink to="/live" label={t('nav.live')} icon={<Camera aria-hidden size={15} />} />
            <TopLink
              to="/playback"
              label={t('nav.playback')}
              icon={<Clock3 aria-hidden size={15} />}
            />
            {hasPermission(session, 'recordings.export') && <TopLink to="/exports" label={t('nav.exports')} icon={<Clock3 aria-hidden size={15} />} />}
          </nav>
        ) : (
          <nav aria-label={t('nav.administration')} className="workspace-nav admin-workspace-nav">
            <TopLink
              to="/admin/cameras"
              label={t('nav.cameras')}
              icon={<Camera aria-hidden size={15} />}
            />
            <TopLink
              to="/admin/analytics"
              label={t('nav.analytics')}
              icon={<ShieldCheck aria-hidden size={15} />}
            />
          </nav>
        )}

        <div className="flex items-center justify-end gap-3">
          <div className="hidden text-right sm:block">
            <ShellClock />
          </div>
          {canAdminister && isAdministration ? (
            <Link
              to="/live"
              className="return-to-live"
              aria-label={t('shell.returnToLive')}
              title={t('shell.returnToLive')}
            >
              <ArrowLeft aria-hidden size={15} />
              <span>{t('nav.live')}</span>
            </Link>
          ) : null}
          <AccountMenu
            {...(name ? { name } : {})}
            onSignOut={() => void signOut()}
            labels={shell.account}
          >
            {canAdminister ? (
              <DropdownMenuItem render={<Link to="/admin/cameras" />}>
                <Settings2 aria-hidden />
                {t('nav.administration')}
              </DropdownMenuItem>
            ) : null}
          </AccountMenu>
        </div>
      </header>
      <main className="min-h-0 flex-1">
        <Outlet />
      </main>
      <Toaster />
    </div>
  );
}

function TopLink({
  to,
  label,
  icon,
}: {
  to: '/exports' | '/live' | '/playback' | '/admin/cameras' | '/admin/analytics';
  label: string;
  icon: React.ReactNode;
}): React.JSX.Element {
  if (to === '/live' || to === '/playback') {
    // Switching between Live and Playback keeps the selected camera.
    return (
      <Link
        to={to}
        search={(prev) => (prev.cameraId ? { cameraId: prev.cameraId } : {})}
        className="top-link"
        activeProps={{ className: 'top-link is-active' }}
      >
        {icon}
        <span>{label}</span>
      </Link>
    );
  }
  return (
    <Link to={to} className="top-link" activeProps={{ className: 'top-link is-active' }}>
      {icon}
      <span>{label}</span>
    </Link>
  );
}

function ShellClock(): React.JSX.Element {
  const [clock, setClock] = React.useState(() => formatTime(Date.now()));

  React.useEffect(() => {
    // Ticks every second so the minute turns over on time; React skips the
    // render while the formatted minute is unchanged.
    const timer = window.setInterval(() => setClock(formatTime(Date.now())), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="font-mono text-xs tracking-[0.08em] text-[var(--color-foreground-2)] tabular-nums">
      {clock}
    </div>
  );
}
